import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

/**
 * The app's single loading identity: the Mowazaf mark inside an orbiting arc.
 *
 * ⚠️ A hand-written twin of this component lives inline in
 * `mowazaf.client/index.html` — it paints the pre-React splash before the JS
 * bundle has parsed, so it cannot import anything. The two MUST stay visually
 * identical; if the splash and the first screen look like different products,
 * the app appears to change identity mid-load. Change one, change the other.
 * Shared rules live in `src/index.css` under "Brand loader".
 *
 * Motion honours `prefers-reduced-motion`: the ring is dropped and the text
 * label becomes the progress signal.
 */

export interface BrandLoaderProps {
  /**
   * `screen` covers the viewport on its own painted background — use it for
   * route-level and session-level waits. `inline` renders bare, for a panel
   * or a card that is already inside a laid-out page.
   */
  variant?: 'screen' | 'inline';
  size?: 'sm' | 'md' | 'lg';
  /**
   * Optional line beneath the mark. Leave unset on the plain splash: a
   * "Loading Mowazaf" caption under the Mowazaf logo restates the logo.
   * Set it when the wait has a *specific* subject worth naming.
   * Always rendered under reduced motion, where it replaces the animation.
   */
  label?: string;
  className?: string;
}

/** Mark height in px, and the orbit diameter that surrounds it. */
const sizeMap = {
  sm: { mark: 24, orbit: 48, stroke: 2 },
  md: { mark: 40, orbit: 76, stroke: 2.5 },
  lg: { mark: 56, orbit: 104, stroke: 3 },
} as const;

export const BrandLoader: React.FC<BrandLoaderProps> = ({
  variant = 'screen',
  size = 'lg',
  label,
  className,
}) => {
  const { t } = useTranslation();
  const { mark, orbit, stroke } = sizeMap[size];
  // The mark is 257x243, not square. Derive width so it is never distorted.
  const markW = Math.round((mark * 257) / 243);
  // Unique per instance: two loaders mounted at once would otherwise share a
  // gradient id and the second one's arc would resolve against the first.
  const gradientId = `mz-loader-arc-${React.useId()}`;

  const r = (orbit - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  // A quarter-turn arc reads as motion without looking like a progress value.
  const arc = circumference * 0.28;

  const body = (
    <div
      className={cn('mz-loader', variant === 'inline' && className)}
      role="status"
      aria-live="polite"
      aria-label={label || t('Loading...')}
    >
      <div className="mz-loader-orbit" style={{ width: orbit, height: orbit }}>
        <svg
          className="mz-loader-ring"
          viewBox={`0 0 ${orbit} ${orbit}`}
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="var(--mowazaf-primary, #304EA1)" />
              <stop offset="100%" stopColor="var(--mowazaf-accent, #4ADDCB)" />
            </linearGradient>
          </defs>
          <circle
            cx={orbit / 2}
            cy={orbit / 2}
            r={r}
            fill="none"
            stroke="var(--mowazaf-primary, #304EA1)"
            strokeOpacity={0.12}
            strokeWidth={stroke}
          />
          <circle
            cx={orbit / 2}
            cy={orbit / 2}
            r={r}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${arc} ${circumference - arc}`}
          />
        </svg>
        {/* Dimensions are set inline, NOT via the height attribute: Tailwind
            preflight's `img { max-width:100%; height:auto }` overrides a
            presentational attribute and the mark then fills the whole orbit.
            Caught on deployed dev, where it rendered at ~2x. */}
        <img
          className="mz-loader-mark"
          src="/images/logo.png"
          style={{ width: markW, height: mark, maxWidth: 'none' }}
          alt=""
          aria-hidden="true"
        />
      </div>
      {label ? (
        <p className="mz-loader-label">{label}</p>
      ) : (
        // Reduced motion drops the ring, so without this the user would be
        // left staring at a static logo with no sign anything is happening.
        // CSS reveals it only under `prefers-reduced-motion: reduce`.
        <p className="mz-loader-label mz-loader-label-rm">
          {t('Starting Mowazaf...')}
        </p>
      )}
    </div>
  );

  if (variant === 'inline') return body;

  return <div className={cn('mz-loader-screen', className)}>{body}</div>;
};

export default BrandLoader;
